import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/OrganizationContext';
import { agentEvents } from '@/lib/agentEvents';
import type { PendingAction } from '@/components/agent/ActionConfirmation';
import type { ContextQuestion } from '@/components/agent/ContextQuestionForm';

interface RetryAction {
  id: string;
  action_type: string;
  params: Record<string, unknown>;
  error_message?: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  toolCalls?: string[];
  isStreaming?: boolean;
  pendingAction?: PendingAction;
  contextQuestion?: ContextQuestion;
  contextQuestionAnswered?: boolean;
  retryAction?: RetryAction;
  retryActioned?: boolean; // true after user clicks retry
}

interface AgentChatContextValue {
  messages: Message[];
  isLoading: boolean;
  sessionId: string | null;
  tokensUsed: number;
  isOpen: boolean;
  isMinimized: boolean;
  isExpanded: boolean;
  openChat: () => void;
  closeChat: () => void;
  setMinimized: (minimized: boolean) => void;
  setExpanded: (expanded: boolean) => void;
  sendMessage: (content: string) => Promise<void>;
  confirmAction: (messageId: string, actionId: string) => Promise<void>;
  cancelAction: (messageId: string, actionId: string) => void;
  answerContextQuestion: (messageId: string, questionId: string, answers: Record<string, string>) => void;
  retryAction: (messageId: string, action: RetryAction) => Promise<void>;
  dismissRetry: (messageId: string, actionId: string) => void;
  clearMessages: () => void;
  cancel: () => void;
}

const AgentChatContext = createContext<AgentChatContextValue | null>(null);

// Helper to get storage key namespaced by organization
const getChatStorageKey = (orgId: string) => `agent_chat_history:${orgId}`;
const getSessionStorageKey = (orgId: string) => `agent_chat_session:${orgId}`;

// Helper to serialize messages for storage
const serializeMessages = (messages: Message[]): string => {
  return JSON.stringify(messages.map(m => ({
    ...m,
    timestamp: m.timestamp.toISOString(),
  })));
};

// Helper to deserialize messages from storage
const deserializeMessages = (stored: string): Message[] => {
  try {
    const parsed = JSON.parse(stored);
    return parsed.map((m: Record<string, unknown>) => ({
      ...m,
      timestamp: new Date(m.timestamp as string),
    }));
  } catch {
    return [];
  }
};

export function AgentChatProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [tokensUsed, setTokensUsed] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const prevOrgIdRef = useRef<string | null>(null);
  const prevIsMspContextRef = useRef<boolean>(false);
  const isInitializedRef = useRef(false);
  const { toast } = useToast();
  
  // Get current organization context (including MSP impersonation)
  const { organization, isMspContext } = useOrganization();

  const generateId = () => crypto.randomUUID();

  // Persist UI state (size/minimise) across navigation/remounts
  useEffect(() => {
    try {
      const raw = localStorage.getItem('agent_chat_ui');
      if (!raw) return;
      const parsed = JSON.parse(raw) as { isMinimized?: boolean; isExpanded?: boolean };
      if (typeof parsed.isMinimized === 'boolean') setIsMinimized(parsed.isMinimized);
      if (typeof parsed.isExpanded === 'boolean') setIsExpanded(parsed.isExpanded);
    } catch {
      // ignore
    }
     
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('agent_chat_ui', JSON.stringify({ isMinimized, isExpanded }));
    } catch {
      // ignore
    }
  }, [isMinimized, isExpanded]);

  // Load chat history when organization changes
  useEffect(() => {
    const currentOrgId = organization?.id;
    if (!currentOrgId) {
      // No org - clear everything
      setMessages([]);
      setSessionId(null);
      setTokensUsed(0);
      return;
    }

    const prevOrgId = prevOrgIdRef.current;
    const prevIsMspContext = prevIsMspContextRef.current;
    
    // Update refs for next comparison
    prevOrgIdRef.current = currentOrgId;
    prevIsMspContextRef.current = isMspContext;

    // If org changed, load chat for new org (or clear if no history)
    if (prevOrgId !== currentOrgId || !isInitializedRef.current) {
      isInitializedRef.current = true;
      
      // Load stored chat history for this org
      try {
        const storedMessages = localStorage.getItem(getChatStorageKey(currentOrgId));
        const storedSession = localStorage.getItem(getSessionStorageKey(currentOrgId));
        
        if (storedMessages) {
          const parsedMessages = deserializeMessages(storedMessages);
          // Only restore if messages are recent (within 24 hours)
          const latestTimestamp = parsedMessages.length > 0 
            ? new Date(parsedMessages[parsedMessages.length - 1].timestamp).getTime()
            : 0;
          const isRecent = Date.now() - latestTimestamp < 24 * 60 * 60 * 1000;
          
          if (isRecent && parsedMessages.length > 0) {
            setMessages(parsedMessages);
            setSessionId(storedSession);
            console.log(`[AgentChat] Restored ${parsedMessages.length} messages for org ${currentOrgId}`);
          } else {
            // Stale messages - clear them
            setMessages([]);
            setSessionId(null);
            localStorage.removeItem(getChatStorageKey(currentOrgId));
            localStorage.removeItem(getSessionStorageKey(currentOrgId));
          }
        } else {
          setMessages([]);
          setSessionId(null);
        }
        setTokensUsed(0);
      } catch (e) {
        console.error('[AgentChat] Failed to load chat history:', e);
        setMessages([]);
        setSessionId(null);
      }
      
      // If MSP was impersonating and now exiting, close the chat panel
      if (prevIsMspContext && !isMspContext && prevOrgId !== null) {
        setIsOpen(false);
      }
    }
  }, [organization?.id, isMspContext]);

  // Persist messages to localStorage whenever they change
  useEffect(() => {
    const currentOrgId = organization?.id;
    if (!currentOrgId || messages.length === 0) return;
    
    // Don't persist if still loading (streaming message incomplete)
    const hasStreamingMessage = messages.some(m => m.isStreaming);
    if (hasStreamingMessage) return;
    
    try {
      localStorage.setItem(getChatStorageKey(currentOrgId), serializeMessages(messages));
      if (sessionId) {
        localStorage.setItem(getSessionStorageKey(currentOrgId), sessionId);
      }
    } catch (e) {
      console.error('[AgentChat] Failed to persist chat history:', e);
    }
  }, [messages, sessionId, organization?.id]);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isLoading) return;
    
    // Safety check: ensure we have correct org context
    const currentOrgId = organization?.id;
    if (!currentOrgId) {
      toast({
        title: 'Error',
        description: 'No organisation context available',
        variant: 'destructive',
      });
      return;
    }

    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    const assistantMessageId = generateId();
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };
    setMessages(prev => [...prev, assistantMessage]);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('You must be logged in to use SupplySteward');
      }

      // Build message history for context (last 10 messages)
      const history = [...messages.slice(-10), userMessage].map(m => ({
        role: m.role,
        content: m.content,
      }));

      console.log(`[AgentChat] Sending message to org ${currentOrgId}, isMspContext: ${isMspContext}`);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-agent-gateway`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            messages: history,
            session_id: sessionId,
            stream: false,
            // CRITICAL: Pass the current organization ID (respects MSP impersonation)
            organization_id: currentOrgId,
            is_msp_context: isMspContext,
          }),
          signal: abortControllerRef.current?.signal,
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get response');
      }

      const data = await response.json();

      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
      }

      if (data.tokens_used) {
        setTokensUsed(prev => prev + data.tokens_used);
      }

      // Parse pending action if present
      const pendingAction = data.pending_action ? {
        ...data.pending_action,
        status: 'pending' as const,
      } : undefined;

      setMessages(prev =>
        prev.map(m =>
          m.id === assistantMessageId
            ? {
                ...m,
                content: data.message,
                toolCalls: data.tool_calls,
                isStreaming: false,
                pendingAction,
                contextQuestion: data.context_question,
              }
            : m
        )
      );

      // Emit events based on tool calls to trigger data refreshes
      if (data.tool_calls && data.tool_calls.length > 0) {
        data.tool_calls.forEach((tool: string) => {
          if (tool.toLowerCase().includes('update') && tool.toLowerCase().includes('supplier')) {
            agentEvents.emit('supplier:updated', { source: 'agent' });
          } else if (tool.toLowerCase().includes('supplier') || tool.toLowerCase().includes('marketplace')) {
            agentEvents.emit('supplier:added', { source: 'agent' });
          }
          if (tool.toLowerCase().includes('poam')) {
            agentEvents.emit('poam:created', { source: 'agent' });
          }
        });
      }

    } catch (error) {
      console.error('SupplySteward error:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'An error occurred';
      
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantMessageId
            ? {
                ...m,
                content: `Sorry, I encountered an error: ${errorMessage}`,
                isStreaming: false,
              }
            : m
        )
      );
      
      toast({
        title: 'SupplySteward Error',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [messages, sessionId, isLoading, toast, organization?.id, isMspContext]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setTokensUsed(0);
    
    // Also clear localStorage for current org
    const currentOrgId = organization?.id;
    if (currentOrgId) {
      try {
        localStorage.removeItem(getChatStorageKey(currentOrgId));
        localStorage.removeItem(getSessionStorageKey(currentOrgId));
      } catch {
        // ignore
      }
    }
  }, [organization?.id]);

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
    setIsLoading(false);
  }, []);

  const openChat = useCallback(() => {
    setIsOpen(true);
    setIsMinimized(false); // Always open in full view, not minimized
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const setMinimized = useCallback((minimized: boolean) => {
    setIsMinimized(minimized);
    if (minimized) {
      // When minimized, keep expanded flag as-is but UI won't use it.
    }
  }, []);

  const setExpanded = useCallback((expanded: boolean) => {
    setIsExpanded(expanded);
  }, []);

  // Confirm a pending action and execute it
  const confirmAction = useCallback(async (messageId: string, actionId: string) => {
    // Find the message with the pending action
    const message = messages.find(m => m.id === messageId);
    if (!message?.pendingAction || message.pendingAction.id !== actionId) return;

    // Update action status to executing
    setMessages(prev =>
      prev.map(m =>
        m.id === messageId && m.pendingAction?.id === actionId
          ? { ...m, pendingAction: { ...m.pendingAction, status: 'executing' as const } }
          : m
      )
    );

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Call the agent gateway with a confirmation message
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-agent-gateway`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            messages: [
              ...messages.slice(-10).map(m => ({ role: m.role, content: m.content })),
              { 
                role: 'user', 
                content: `EXECUTE_CONFIRMED_ACTION:${JSON.stringify({
                  action_type: message.pendingAction.type,
                  params: message.pendingAction.params,
                })}` 
              }
            ],
            session_id: sessionId,
            stream: false,
            organization_id: organization?.id,
            is_msp_context: isMspContext,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to execute action');
      }

      const data = await response.json();

      // Update action status to confirmed and add response
      setMessages(prev => {
        const updated = prev.map(m =>
          m.id === messageId && m.pendingAction?.id === actionId
            ? { ...m, pendingAction: { ...m.pendingAction, status: 'confirmed' as const } }
            : m
        );

        // Add the response message
        const responseMessage: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: data.message,
          timestamp: new Date(),
          toolCalls: data.tool_calls,
          contextQuestion: data.context_question,
        };

        return [...updated, responseMessage];
      });

      if (data.tokens_used) {
        setTokensUsed(prev => prev + data.tokens_used);
      }

      // Emit events based on tool calls to trigger data refreshes
      if (data.tool_calls && data.tool_calls.length > 0) {
        data.tool_calls.forEach((tool: string) => {
          if (tool.toLowerCase().includes('update') && tool.toLowerCase().includes('supplier')) {
            agentEvents.emit('supplier:updated', { source: 'agent_action' });
          } else if (tool.toLowerCase().includes('supplier') || tool.toLowerCase().includes('marketplace')) {
            agentEvents.emit('supplier:added', { source: 'agent_action' });
          }
          if (tool.toLowerCase().includes('poam')) {
            agentEvents.emit('poam:created', { source: 'agent_action' });
          }
        });
      }

    } catch (error) {
      console.error('Action execution error:', error);
      
      // Inject retry action instead of just reverting to pending
      const retryActionData: RetryAction = {
        id: crypto.randomUUID(),
        action_type: message.pendingAction.type,
        params: message.pendingAction.params,
        error_message: error instanceof Error ? error.message : 'Unknown error',
      };
      
      setMessages(prev =>
        prev.map(m =>
          m.id === messageId && m.pendingAction?.id === actionId
            ? { 
                ...m, 
                pendingAction: { ...m.pendingAction, status: 'cancelled' as const },
                retryAction: retryActionData,
              }
            : m
        )
      );

      toast({
        title: 'Action Failed',
        description: error instanceof Error ? error.message : 'Failed to execute action',
        variant: 'destructive',
      });
    }
  }, [messages, sessionId, organization?.id, isMspContext, toast]);

  // Cancel a pending action
  const cancelAction = useCallback((messageId: string, actionId: string) => {
    setMessages(prev =>
      prev.map(m =>
        m.id === messageId && m.pendingAction?.id === actionId
          ? { ...m, pendingAction: { ...m.pendingAction, status: 'cancelled' as const } }
          : m
      )
    );
  }, []);

  // Answer a context question and execute the action directly
  const answerContextQuestion = useCallback(async (messageId: string, questionId: string, answers: Record<string, string>) => {
    // Find the message with the context question to get hidden_data
    const message = messages.find(m => m.id === messageId);
    const contextQuestion = message?.contextQuestion;
    
    console.log('[AgentChat] Context question answers:', answers);
    console.log('[AgentChat] Hidden data:', contextQuestion?.hidden_data);
    
    // Mark the question as answered in the message
    setMessages(prev =>
      prev.map(m =>
        m.id === messageId
          ? { ...m, contextQuestionAnswered: true }
          : m
      )
    );

    // Determine the action type based on hidden_data or answers
    // If there's a marketplace_listing_id, it's an add_supplier_from_marketplace action
    // If there's a domain_or_email or domain, it's a request_new_supplier action
    const marketplaceListingId = answers.marketplace_listing_id || contextQuestion?.hidden_data?.marketplace_listing_id;
    const domain = answers.domain_or_email || answers.domain || contextQuestion?.hidden_data?.domain;
    
    let actionType: string;
    let params: Record<string, unknown>;
    
    // Determine the correct status - check supplier_status field first (from form), fallback to 'active'
    // CRITICAL: If user explicitly selected a value, use it. Don't default to 'onboarding' unless they chose it.
    const selectedStatus = answers.supplier_status || 'active';
    console.log('[AgentChat] Selected status:', selectedStatus, 'Raw answers.supplier_status:', answers.supplier_status);
    
    if (marketplaceListingId) {
      actionType = 'add_supplier_from_marketplace';
      params = {
        marketplace_listing_id: marketplaceListingId,
        criticality_tier: answers.criticality_tier,
        supplier_status: selectedStatus,
      };
      console.log('[AgentChat] add_supplier_from_marketplace params:', params);
    } else if (domain) {
      actionType = 'request_new_supplier';
      params = {
        domain: domain,
        criticality_tier: answers.criticality_tier,
        supplier_status: selectedStatus,
      };
      console.log('[AgentChat] request_new_supplier params:', params);
    } else {
      // Fallback: send as regular message if we can't determine action type
      const answerParts = Object.entries(answers)
        .filter(([_, value]) => value.trim())
        .map(([key, value]) => {
          const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          return `${label}: ${value}`;
        });
      const formattedAnswer = answerParts.join(', ');
      if (formattedAnswer) {
        sendMessage(formattedAnswer);
      }
      return;
    }

    // Create a user message showing what was submitted
    const answerSummary = Object.entries(answers)
      .filter(([key, value]) => value.trim() && !key.includes('listing_id') && !key.includes('domain'))
      .map(([key, value]) => {
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return `${label}: ${value}`;
      }).join(', ');

    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content: answerSummary || 'Confirmed',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMessage]);

    // Show loading state
    const assistantMessageId = generateId();
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };
    setMessages(prev => [...prev, assistantMessage]);
    setIsLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Send EXECUTE_CONFIRMED_ACTION to bypass model and execute tool directly
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-agent-gateway`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            messages: [
              { 
                role: 'user', 
                content: `EXECUTE_CONFIRMED_ACTION:${JSON.stringify({
                  action_type: actionType,
                  params: params,
                })}` 
              }
            ],
            session_id: sessionId,
            stream: false,
            organization_id: organization?.id,
            is_msp_context: isMspContext,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to execute action');
      }

      const data = await response.json();

      // Check if there are pending suppliers to add (from hidden_data)
      const pendingSuppliers = contextQuestion?.hidden_data?.pending_suppliers;
      
      // Update the assistant message with result
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantMessageId
            ? { 
                ...m, 
                content: data.message, 
                isStreaming: false, 
                toolCalls: data.tool_calls,
                contextQuestion: data.context_question, // Include next form if AI returned one
              }
            : m
        )
      );

      if (data.tokens_used) {
        setTokensUsed(prev => prev + data.tokens_used);
      }

      // Emit events to trigger UI refreshes
      if (actionType.includes('supplier')) {
        agentEvents.emit('supplier:added', { source: 'agent_context_question' });
      }
      
      // If there are pending suppliers, send a follow-up to the AI to get the next form
      // Format: "SupplierName:UUID" or "SupplierName:UUID,OtherName:UUID2"
      if (pendingSuppliers && typeof pendingSuppliers === 'string' && pendingSuppliers.trim()) {
        console.log('[AgentChat] Pending suppliers to add:', pendingSuppliers);
        
        // Parse the first pending supplier
        const pending = pendingSuppliers.split(',')[0]?.trim();
        if (pending) {
          const [supplierName, listingId] = pending.split(':').map(s => s.trim());
          
          if (supplierName && listingId) {
            // Calculate remaining pending suppliers for the next form
            const remainingPending = pendingSuppliers.split(',').slice(1).join(',').trim();
            const remainingCount = remainingPending ? remainingPending.split(',').length : 0;
            const totalCount = remainingCount + 2; // +2 because this is the 2nd of N
            const currentIndex = totalCount - remainingCount; // Should be 2 for second supplier
            
            // Brief delay to let the UI update, then add the next form
            setTimeout(() => {
              // Add a system-like user message that triggers the next form
              const followUpUserMessage: Message = {
                id: generateId(),
                role: 'user',
                content: `Continue adding ${supplierName}`,
                timestamp: new Date(),
              };
              
              // Create the assistant response with the next form directly
              const nextFormMessage: Message = {
                id: generateId(),
                role: 'assistant',
                content: `**Adding ${supplierName} (${currentIndex} of ${totalCount}):**\n\nPlease select your preferences for this supplier:`,
                timestamp: new Date(),
                contextQuestion: {
                  id: `add_supplier_${Date.now()}`,
                  hidden_data: {
                    marketplace_listing_id: listingId,
                    company_name: supplierName,
                    // Remove the first pending from the list for remaining
                    pending_suppliers: remainingPending || undefined,
                  },
                  fields: [
                    {
                      id: 'criticality_tier',
                      label: 'Criticality Tier',
                      type: 'select',
                      options: [
                        { value: 'critical', label: 'Critical' },
                        { value: 'high', label: 'High' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'low', label: 'Low' },
                      ],
                      required: true,
                      defaultValue: 'medium', // Sensible default
                    },
                    {
                      id: 'supplier_status',
                      label: 'Supplier Status',
                      type: 'select',
                      options: [
                        { value: 'active', label: 'Active' },
                        { value: 'onboarding', label: 'Onboarding' },
                      ],
                      required: true,
                      defaultValue: 'active', // IMPORTANT: Default to active, not onboarding
                    },
                  ],
                },
              };
              
              setMessages(prev => [...prev, followUpUserMessage, nextFormMessage]);
            }, 500);
          }
        }
      }

    } catch (error) {
      console.error('Context question action error:', error);
      
      // Inject retry action so user can retry the failed action
      const retryActionData: RetryAction = {
        id: crypto.randomUUID(),
        action_type: actionType,
        params: params,
        error_message: error instanceof Error ? error.message : 'Unknown error',
      };
      
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantMessageId
            ? { 
                ...m, 
                content: `Sorry, I couldn't complete that action: ${error instanceof Error ? error.message : 'Unknown error'}`, 
                isStreaming: false,
                retryAction: retryActionData,
              }
            : m
        )
      );

      toast({
        title: 'Action Failed',
        description: error instanceof Error ? error.message : 'Failed to execute action',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [messages, sessionId, organization?.id, isMspContext, toast, sendMessage]);

  // Retry a failed action
  const retryActionHandler = useCallback(async (messageId: string, action: RetryAction) => {
    // Mark the retry as actioned
    setMessages(prev =>
      prev.map(m =>
        m.id === messageId
          ? { ...m, retryActioned: true }
          : m
      )
    );

    // Show loading state
    const assistantMessageId = generateId();
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };
    setMessages(prev => [...prev, assistantMessage]);
    setIsLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-agent-gateway`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            messages: [
              { 
                role: 'user', 
                content: `EXECUTE_CONFIRMED_ACTION:${JSON.stringify({
                  action_type: action.action_type,
                  params: action.params,
                })}` 
              }
            ],
            session_id: sessionId,
            stream: false,
            organization_id: organization?.id,
            is_msp_context: isMspContext,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to execute action');
      }

      const data = await response.json();

      // Check if retry failed again - inject another retry action
      if (data.retry_action) {
        setMessages(prev =>
          prev.map(m =>
            m.id === assistantMessageId
              ? { 
                  ...m, 
                  content: data.message, 
                  isStreaming: false, 
                  toolCalls: data.tool_calls,
                  retryAction: data.retry_action,
                }
              : m
          )
        );
      } else {
        setMessages(prev =>
          prev.map(m =>
            m.id === assistantMessageId
              ? { 
                  ...m, 
                  content: data.message, 
                  isStreaming: false, 
                  toolCalls: data.tool_calls,
                  contextQuestion: data.context_question,
                }
              : m
          )
        );
      }

      if (data.tokens_used) {
        setTokensUsed(prev => prev + data.tokens_used);
      }

      // Emit events to trigger UI refreshes
      if (action.action_type.includes('supplier')) {
        agentEvents.emit('supplier:added', { source: 'agent_retry' });
      }

    } catch (error) {
      console.error('Retry action error:', error);
      
      // Inject another retry action for the failed retry
      const newRetryAction: RetryAction = {
        id: crypto.randomUUID(),
        action_type: action.action_type,
        params: action.params,
        error_message: error instanceof Error ? error.message : 'Unknown error',
      };
      
      setMessages(prev =>
        prev.map(m =>
          m.id === assistantMessageId
            ? { 
                ...m, 
                content: `Retry failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 
                isStreaming: false,
                retryAction: newRetryAction,
              }
            : m
        )
      );

      toast({
        title: 'Retry Failed',
        description: error instanceof Error ? error.message : 'Failed to retry action',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, organization?.id, isMspContext, toast]);

  // Dismiss a retry action
  const dismissRetry = useCallback((messageId: string, actionId: string) => {
    setMessages(prev =>
      prev.map(m =>
        m.id === messageId && m.retryAction?.id === actionId
          ? { ...m, retryActioned: true }
          : m
      )
    );
  }, []);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return (
    <AgentChatContext.Provider
      value={{
        messages,
        isLoading,
        sessionId,
        tokensUsed,
        isOpen,
        isMinimized,
        isExpanded,
        openChat,
        closeChat,
        setMinimized,
        setExpanded,
        sendMessage,
        confirmAction,
        cancelAction,
        answerContextQuestion,
        retryAction: retryActionHandler,
        dismissRetry,
        clearMessages,
        cancel,
      }}
    >
      {children}
    </AgentChatContext.Provider>
  );
}

/** The chat state, or null outside an AgentChatProvider (a presentational control's story or test). */
export function useOptionalAgentChat() {
  return useContext(AgentChatContext);
}

export function useAgentChat() {
  const context = useContext(AgentChatContext);
  if (!context) {
    throw new Error('useAgentChat must be used within an AgentChatProvider');
  }
  return context;
}
